<template>
    <b-tabs card pills :nav-wrapper-class="{ 'd-none': hideTabs }" v-model="activeTab">
        <b-tab title="Task Details">
            <b-card>
                <b-form-group>
                    <template v-if="!value.id && templates.length && !value.title">
                        <a variant="link" href="#" @click.prevent="" id="createFromTemplate" class="pull-right">
                            Create from Template...
                        </a>
                        <b-popover :show.sync="showTemplateSelectionPopover" target="createFromTemplate"
                            triggers="click blur" placement="bottom" custom-class="task-template-popover">
                            <b-input v-model="filterTemplates" type="search" placeholder="Filter Templates"
                                class="mb-2" />

                            <div class="scroller">
                                <b-list-group>
                                    <b-list-group-item v-for="t in filteredTemplates" :key="t.id" href="#"
                                        @click="applyTemplate(t)">
                                        {{ t.title }}
                                    </b-list-group-item>
                                </b-list-group>
                            </div>
                        </b-popover>
                    </template>
                    <label>Title*</label>
                    <b-form-input :value="value.title" :readonly="fieldsDisabled || disableAdHocFields"
                        @input="$emit('input', { ...value, title: $event })" maxlength="300" />
                </b-form-group>

                <b-form-group label="Description">
                    <b-form-textarea :value="value.description" :readonly="fieldsDisabled || disableAdHocFields"
                        @input="$emit('input', { ...value, description: $event })" rows="6" />
                </b-form-group>

                <b-row>
                    <b-col v-if="!value.isScheduled && !value.isTemplate" lg="3">
                        <b-form-group label="Status">
                            <b-dropdown variant="link" size="sm" block class="status" no-caret :disabled="fieldsDisabled">
                                <template #button-content>
                                    <TaskStatusBadge :value="activeStatus" />
                                </template>

                                <b-dropdown-item @click="updateActiveStatus(UserTaskStatus.new)">
                                    <TaskStatusBadge :value="UserTaskStatus.new" />
                                </b-dropdown-item>
                                <b-dropdown-item @click="updateActiveStatus(UserTaskStatus.inProgress)">
                                    <TaskStatusBadge :value="UserTaskStatus.inProgress" />
                                </b-dropdown-item>
                                <b-dropdown-item v-if="value.id" @click="updateActiveStatus(UserTaskStatus.complete)">
                                    <TaskStatusBadge :value="UserTaskStatus.complete" />
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-form-group>
                    </b-col>
                    <b-col :lg="!value.isScheduled && !value.isTemplate ? 3 : 4">
                        <b-form-group label="Store">
                            <SearchComponent :key="activeStore?.id" v-model="activeStore" label="" search-u-r-l="/Store"
                                :prepopulate="true" clearable :disabled="fieldsDisabled || disableAdHocFields" />
                        </b-form-group>
                    </b-col>
                    <b-col :lg="!value.isScheduled && !value.isTemplate ? 6 : 8">
                        <label>Assigned To</label>
                        <div class="pull-right d-flex" style="gap: 8px;">
                            <a v-if="!fieldsDisabled && !isAssignedToMe" href="#" @click.prevent="assignToMe">
                                <i class="far fa-user" />
                                Assign to Me
                            </a>
                            <a v-if="!!primaryPatient && !fieldsDisabled" href="#" @click.prevent="assignToPrimaryPatient">
                                <i class="fa fa-user" />
                                To Patient
                            </a>
                            <a v-if="!!primaryPrescriber && !fieldsDisabled" href="#" @click.prevent="assignToPrimaryPrescriber">
                                <i class="fa fa-user-md" />
                                To Prescriber
                            </a>
                        </div>
                        <b-form-group>
                            <multiselect v-model="activeAssignedTo" :options="assignToOptions" :multiple="allowMultipleAssignments"
                                group-values="options" group-label="type" label="name" track-by="id" :allow-empty="true"
                                :loading="loadingAssignToOptions" :disabled="fieldsDisabled" @search-change="assignToSearch">

                                <template #singleLabel="data">
                                    <p class="m-0" v-html="data.option.name"></p>
                                </template>

                                <template #option="data">
                                    <i v-if="data.option.$groupLabel === 'Teams'" class="fa fa-users"></i>
                                    <i v-if="data.option.$groupLabel === 'Users'" class="fa fa-user"></i>
                                    <i v-if="data.option.$groupLabel === 'Patients'" class="far fa-user"></i>
                                    <i v-if="data.option.$groupLabel === 'Prescribers'" class="fa fa-notes-medical"></i>
                                    {{ data.option.$groupLabel }}
                                    <p class="m-0" v-html="data.option.name"></p>
                                </template>

                                <template #tag="data">
                                    <b-badge v-if="data.option?.isTeam" variant="primary" class="mr-1 my-0">
                                        <i class="fa fa-users"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-if="data.option?.isPatient" variant="warning" class="mr-1 my-0">
                                        <i class="far fa-user"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-if="data.option?.isPrescriber" variant="danger" class="mr-1 my-0">
                                        <i class="fa fa-notes-medical"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-if="data.option?.isUser" variant="info" class="mr-1 my-0">
                                        <i class="fa fa-user"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                </template>
                            </multiselect>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mb-2">
                    <b-col>
                        <label>
                            Watchers
                            <HelpPopup>
                                <template v-if="value.isScheduled">
                                    Watchers will automatically get added to each task created from this schedule.
                                </template>
                                <template v-else-if="value.isTemplate">
                                    Watchers will automatically get added to each task created from this template.
                                </template>
                                <template v-else>
                                    Watchers will get notified when this task completes.
                                </template>
                            </HelpPopup>
                        </label>
                        <a href="#"
                            v-if="!value.watchers?.find(w => w.userName?.toLowerCase() == $user.userName.toLowerCase()) && !fieldsDisabled"
                            @click.prevent="addMeAsWatcher" class="pull-right mt-1">
                            <i class="fa fa-eye" />
                            Add Me as Watcher
                        </a>
                        <b-form-group>
                            <multiselect v-model="activeWatchers" :options="watchersOptions" :multiple="true"
                                group-values="options" group-label="type" label="name" track-by="id" :disabled="fieldsDisabled">

                                <template #option="data">
                                    <i v-if="data.option.$groupLabel === 'Teams'" class="fa fa-users"></i>
                                    <i v-if="data.option.$groupLabel === 'Users'" class="fa fa-user"></i>
                                    {{ data.option.$groupLabel }}
                                    {{ data.option.name }}
                                </template>

                                <template #tag="data">
                                    <b-badge v-if="data.option.isTeam" variant="primary" class="mr-1 my-0">
                                        <i class="fa fa-users"></i>
                                        {{ data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-else variant="info" class="mr-1 my-0">
                                        <i class="fa fa-user"></i>
                                        {{ data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                </template>
                            </multiselect>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-check v-model="value.blockCheckout" switch :disabled="fieldsDisabled || disableAdHocFields">
                            Block Checkout
                            <HelpPopup>
                                <template v-if="value.isScheduled">
                                    When enabled, tasks created from this schedule will block checkout for any patients
                                    and/or prescriptions linked to the underlying task until it is completed or expired.
                                </template>
                                <template v-else-if="value.isTemplate">
                                    When enabled, tasks created from this template will block checkout for any patients
                                    and/or prescriptions linked to the underlying task until it is completed or expired.
                                </template>
                                <template v-else>
                                    When enabled, this task will block checkout for any linked patients and/or
                                    prescriptions until it is completed or expired.
                                </template>
                            </HelpPopup>
                        </b-check>
                    </b-col>
                </b-row>
            </b-card>

            <b-card v-if="!value.isScheduled && !value.isTemplate">
                <b-row cols-lg="2">
                    <b-col>
                        <b-form-group label="Start Date*">
                            <el-date-picker v-model="value.startDate" format="MM/dd/yyyy" type="date" :disabled="fieldsDisabled" />
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group>
                            <label>Due Date*</label>
                            <b-checkbox switch class="pull-right" :checked="value.dueAtTime"
                                :disabled="fieldsDisabled" @change="$emit('input', { ...value, dueAtTime: $event })">
                                Specify Due Time
                            </b-checkbox>
                            <b-row>
                                <b-col>
                                    <el-date-picker format="MM/dd/yyyy" type="date"
                                        :value="moment.utc(value.dueDate).local()" @input="handleDateChange" :disabled="fieldsDisabled" />
                                </b-col>
                                <b-col v-if="value.dueAtTime">
                                    <b-form-timepicker size="sm" minutes-step="15"
                                        :value="moment.utc(value.dueDate).local().format('HH:mm:ss')"
                                        :disabled="fieldsDisabled" @input="handleTimeChange" />
                                </b-col>
                            </b-row>
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group>
                            <label>Expiration Date*</label>
                            <HelpPopup>
                                If task is not completed by expiration date, the task will transition to "Expired"
                                status.
                            </HelpPopup>
                            <el-date-picker format="MM/dd/yyyy" type="date" v-model="value.expiredOnDate" clearable :disabled="fieldsDisabled" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-card>

            <template v-if="value.isScheduled">
                <b-card cols-lg="4" cols="12">
                    <b-row>
                        <b-col>
                            <b-form-group>
                                <b-checkbox switch v-model="value.enabled" :disabled="fieldsDisabled">
                                    Enabled
                                </b-checkbox>
                            </b-form-group>

                            <b-form-group label="Start Date*">
                                <el-date-picker v-model="value.startDate" format="MM/dd/yyyy" type="date" :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group label="Due Date">
                                <div class="d-flex" style="gap: 6px;">
                                    <b-input style="width: 60px" min="0" :value="dueDateDaysAfter"
                                        :disabled="fieldsDisabled" @input="handleDueAfterCreation" type="number" />
                                    Day(s) After Creation
                                </div>
                            </b-form-group>
                            <b-checkbox switch :checked="value.dueAtTime"
                                :disabled="fieldsDisabled" @change="$emit('input', { ...value, dueAtTime: $event })">
                                Specify Due Time
                            </b-checkbox>
                            <b-form-timepicker v-if="value.dueAtTime" size="sm" minutes-step="15"
                                :value="moment.utc(value.dueDate).local().format('HH:mm:ss')" @input="handleTimeChange"
                                :disabled="fieldsDisabled" dropup />
                        </b-col>
                        <b-col>
                            <b-checkbox switch :checked="!!value.expiredOnDate"
                                @change="handleExpiresAfterCreation($event ? '1' : undefined)">
                                Expires
                            </b-checkbox>
                            <div v-if="expirationDaysAfter" class="d-flex" style="gap: 6px;">
                                <b-input style="width: 60px" min="0" :value="expirationDaysAfter"
                                    @input="handleExpiresAfterCreation" type="number" />
                                Day(s) After Creation
                            </div>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card>
                    <b-row>
                        <b-col cols="12" lg="4">
                            <b-form-group label="Schedule Type">
                                <b-select :value="value.repeatMode" :options="getScheduleTypeOptions"
                                    :disabled="fieldsDisabled" @input="$emit('input', { ...value, repeatMode: $event, repeatSchedule: '' })" />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" lg="8">
                            <b-form-group label="Schedule*">
                                <div v-if="value.repeatMode === 0" class="d-flex" style="gap: 6px;">
                                    Every <b-input type="number" style="width: 60px;" min="1"
                                        :value="value.repeatSchedule" :disabled="fieldsDisabled"
                                        @input="$emit('input', { ...value, repeatSchedule: $event })" />
                                    Day(s)
                                </div>
                                <div v-if="value.repeatMode === 1" class="d-flex flex-wrap" style="gap: 6px;">
                                    <b-checkbox
                                        v-for="(d, i) in ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']"
                                        switch size="sm" :key="d"
                                        :checked="value.repeatSchedule?.includes(i.toString())"
                                        :disabled="fieldsDisabled" @change="handleDaySchedule(i)">
                                        {{ d }}
                                    </b-checkbox>
                                </div>
                                <div v-if="value.repeatMode === 2">
                                    <div class="schedule-month-grid">
                                        <b-checkbox v-for="d in Array.from({ length: 31 }, (_, i) => i + 1)" switch
                                            size="sm" :key="d"
                                            :checked="value.repeatSchedule?.split(',').includes(d.toString())"
                                            @change="handleMonthSchedule(d)">
                                            {{ d > 28 ? d + "*" : d }}
                                        </b-checkbox>
                                    </div>

                                    <b-alert :show="value.repeatSchedule?.split(',').some(s => Number(s) > 28)"
                                        style="line-height: 1.3;" variant="info">
                                        <strong>
                                            *On months that have less than this many days, this task will be created on
                                            the last day of the month.
                                        </strong>
                                    </b-alert>
                                </div>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
            </template>
            <template v-else-if="value.isTemplate">
                <b-card>
                    <b-row cols-lg="3" cols="1">
                        <b-col>
                            <b-form-group>
                                <b-checkbox switch v-model="value.enabled">
                                    Enabled
                                </b-checkbox>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group label="Due Date">
                                <div class="d-flex" style="gap: 6px;">
                                    <b-input style="width: 60px" min="0" :value="dueDateDaysAfter"
                                        :disabled="fieldsDisabled" @input="handleDueAfterCreation" type="number" />
                                    Day(s) After Creation
                                </div>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-checkbox switch :checked="!!value.expiredOnDate"
                                @change="handleExpiresAfterCreation($event ? '1' : undefined)">
                                Expires
                            </b-checkbox>
                            <div v-if="expirationDaysAfter" class="d-flex" style="gap: 6px;">
                                <b-input style="width: 60px" min="0" :value="expirationDaysAfter"
                                    @input="handleExpiresAfterCreation" type="number" />
                                Day(s) After Creation
                            </div>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card>
                    <b-row cols-lg="3" cols="1">
                        <b-col>
                            <b-form-group>
                                <label>Limit to Teams</label>
                                <multiselect v-model="limitToTeams" :options="teams" :multiple="true" label="name"
                                    track-by="id" :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group>
                                <label>Limit to Programs</label>
                                <multiselect v-model="limitToPrograms" :options="programs" :multiple="true" label="name"
                                    track-by="id" :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group>
                                <label>Limit to Rx Statuses</label>
                                <multiselect v-model="limitToRxStatuses" :options="rxStatusOptions" :multiple="true"
                                    label="name" track-by="id" :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
            </template>
            <template v-else>
                <b-row>
                    <b-col class="d-flex align-items-stretch">
                        <b-card>
                            <label>Linked Patients</label>
                            <b-table :items="activePatients"
                                :fields="[{ key: 'name', label: 'Patients' }, { key: 'action', label: '', class: 'action-col' }]"
                                foot-clone class="mb-0" thead-class="d-none">
                                <template #cell(name)="data">
                                    <div v-html="getPatientHtml(data.item)"></div>
                                </template>
                                <template #cell(action)="data">
                                    <b-button size="sm" variant="danger" :disabled="fieldsDisabled" @click="removePatient(data.item)">
                                        <i class="fa fa-trash" />
                                    </b-button>
                                </template>
                                <template #foot(name)="">
                                    <SearchComponent :key="activePatients.length" v-model="activePatient"
                                        :hide-label="true" search-u-r-l="/Patient" :prepopulate="false"
                                        :disabled="fieldsDisabled" :results-modifier="patientAddSearchResultFilter" />
                                </template>
                                <template #foot(action)="">
                                    <b-button size="sm" variant="success" disabled class="mb-0">
                                        <i class="fa fa-plus"></i>
                                    </b-button>
                                </template>
                            </b-table>
                        </b-card>
                    </b-col>
                    <b-col class="d-flex align-items-stretch">
                        <b-card>
                            <label>Linked Prescriptions</label>
                            <b-table :items="activePrescriptions"
                                :fields="[{ key: 'name', label: 'Prescriptions' }, { key: 'action', label: '', class: 'action-col' }]"
                                foot-clone class="mb-0" thead-class="d-none">
                                <template #cell(name)="data">
                                    <div v-html="getPrescriptionHtml(data.item)"></div>
                                </template>
                                <template #cell(action)="data">
                                    <b-button size="sm" variant="danger" :disabled="fieldsDisabled" @click="removePrescription(data.item)">
                                        <i class="fa fa-trash" />
                                    </b-button>
                                </template>
                                <template #foot(name)="">
                                    <SearchComponent :key="activePrescriptions.length" v-model="activePrescription"
                                        :hide-label="true" search-u-r-l="/Prescription" :prepopulate="false"
                                        :disabled="fieldsDisabled" :results-modifier="prescriptionAddSearchResultFilter"
                                        :option-label="prescriptionOptionLabel"/>
                                </template>
                                <template #foot(action)="">
                                    <b-button size="sm" variant="success" disabled class="mb-0">
                                        <i class="fa fa-plus"></i>
                                    </b-button>
                                </template>
                            </b-table>
                        </b-card>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-card v-if="value.id">
                            <NotesForm ref="notesForm" :entity-id="value.id" :entity-type="EntityType.UserTask"
                                save-button-label="Add Note" text-box-label="Note" @saved="refreshNotes" />
                            <NotesList ref="notesList" :entity-id="value.id" :entity-type="EntityType.UserTask"
                                :page-size="5" />
                        </b-card>
                    </b-col>
                </b-row>
                <!-- <b-row>
                    <b-col>
                        Editor value:
                        {{ value }}
                    </b-col>
                </b-row> -->
            </template>

            <b-card v-if="isNewTask || value.isScheduled || value.isTemplate || activeCompletionForm?.id">
                Task Completion Form
                <SearchComponent v-model="activeCompletionForm" :hide-label="true" :clearable="true" :prepopulate="true"
                    search-u-r-l="/Task/completion-form" :params="{ onlyActive: true }"
                    :disabled="!(isNewTask || value.isScheduled || value.isTemplate)" />
            </b-card>
        </b-tab>
        <b-tab v-if="showQuestionsTab" title="Completion Questions">
            <TaskQuestions :questions="value.completionQuestionSet?.questions"
                @input="handleQuestionsUpdate"
                :disabled="fieldsDisabled || disableAdHocFields || (!(value.isScheduled || value.isTemplate || !value.id))"
                :editing="(!isRegularTask || isNewTask)" :answers="value.completionAnswers"
                :show-errors="showCompletionQuestionErrors" />
        </b-tab>
        <b-tab v-if="value.completionFormImageId" title="Completion Form">
            <div style="position: relative;">
                <ImageComponent style="height:calc(100vh - 300px);" :imageID="value.completionFormImageId" />

                <div class="w-100 text-center" style="position: absolute; top: -22px; font-size: 1.9em;">
                    <div class="d-inline-block rounded">
                        <b-badge v-if="value.completionFormImageAccepted === true" variant="success" class="px-4"
                            :id="value.signatureData ? 'accepted-badge-' + value.id : null"
                            :style="value.signatureData ? 'cursor: pointer;' : null">
                            <div class="my-2">
                                Accepted
                            </div>
                            <div v-if="value.signatureData"
                                style="font-size: 0.7em; font-weight: 600; text-decoration: underline;" class="mb-1">
                                <i class="fa fa-signature mr-1"></i>View Signature...
                            </div>
                        </b-badge>
                        <b-badge v-if="value.completionFormImageAccepted === false" variant="danger" class="px-4">
                            Rejected
                        </b-badge>
                    </div>
                </div>

                <!-- Signature Popover -->
                <b-popover v-if="value.signatureData && value.completionFormImageAccepted === true"
                    :target="'accepted-badge-' + value.id" triggers="click" placement="bottom"
                    title="Electronic Signature">
                    <div class="signature-popover-content">
                        <div class="mb-3">
                            <img :src="value.signatureData"
                                style="max-width: 200px; height: auto; border: 1px solid #dee2e6; border-radius: 4px;"
                                alt="Electronic Signature" />
                        </div>
                        <div class="text-center">
                            <strong>Date Signed:</strong><br>
                            <span>{{ value.signatureDate | formatDate("L") }}</span>
                        </div>
                    </div>
                </b-popover>
            </div>

        </b-tab>
    </b-tabs>
</template>

<script lang="ts">
import { DatePicker } from "element-ui";
import Multiselect from 'vue-multiselect';
import { Component, Vue, Watch, Ref, Prop } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import { UserTask, UserTaskStatus, UserTaskAnswer, UserTaskQuestion } from "@/models/UserTask";
import { Store } from "@/models/Store";
import { Patient } from "@/models/Patient";
import { Prescription, RefillStatusCode } from "@/models/Prescription";
import { Team } from "@/models/Team";
import { User } from "@/models/User";
import TaskStatusBadge from "./TaskStatusBadge.vue";
import { EntityType } from "@/models/EntityType";
import NotesList from "../Note/NotesList.vue";
import NotesForm from "../Note/NotesForm.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import moment from "moment";
import HelpPopup from "../HelpPopup.vue";
import { Prescriber } from "@/models/Prescriber";
import { debounce } from "throttle-debounce";
import Axios, { CancelTokenSource } from "axios";
import TaskQuestions from "./TaskQuestions.vue";
import { Program } from "@/models/Program";
import { BPopover } from "bootstrap-vue";
import { CustomFaxTemplateBase } from "@/models/FaxTemplates";
import ImageComponent from "../Prescription/ImageComponent.vue";

@Component({
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        Multiselect,
        TaskStatusBadge,
        NotesForm,
        NotesList,
        HelpPopup,
        TaskQuestions,
        ImageComponent
    }
})
export default class TaskEditor extends Vue {

    @Prop({ required: true }) protected value!: UserTask;
    @Prop({ default: false }) protected showCompletionQuestionErrors!: boolean;
    @Prop({ default: false }) protected fieldsDisabled!: boolean;

    @Ref("notesList") protected notesList!: any;
    @Ref("notesForm") protected notesForm!: any;

    protected teams: Team[] = [];
    protected users: User[] = [];
    protected templates: UserTask[] = [];
    protected programs: Program[] = [];

    private activeAssignedTo: any = [];
    private activeWatchers: any = [];
    private activeStore = new Store();
    private activePatients: Patient[] = [];
    private activePrescriptions: Prescription[] = [];
    private activePatient = new Patient();
    private activePrescription = new Prescription();
    private activeStatus: UserTaskStatus | null = UserTaskStatus.new;
    private activeCompletionForm: CustomFaxTemplateBase = new CustomFaxTemplateBase();

    private patientOptions: Patient[] = [];
    private loadingPatientOptions = false;

    private prescriberOptions: Prescriber[] = [];
    private loadingPrescriberOptions = false;

    protected activeAnswers: UserTaskAnswer[] = [];

    protected filterTemplates = "";

    protected activeTab = 0;

    private allowMultipleAssignments = false;

    UserTaskStatus = UserTaskStatus;
    EntityType = EntityType;

    moment = moment;

    async mounted() {
        // Load configuration FIRST before setting up the task
        await this.loadConfiguration();

        if (!this.value.id) this.getTemplates();
        this.getTeams();
        this.getUsers();
        if (this.value.isTemplate) {
            this.getPrograms();
        }

        this.setupTask();
    }

    async loadConfiguration() {
        try {
            const response = await this.$http.get<boolean>("/Configuration/AllowMultipleTaskAssignments");
            this.allowMultipleAssignments = response.data;
        } catch (err) {
            console.error("Failed to load allowMultipleTaskAssignments configuration:", err);
            // Default to false on error
            this.allowMultipleAssignments = false;
        }
    }

    setupTask() {
        this.activeStore = Object.assign(new Store(), { id: this.value.storeId });
        this.activePrescriptions = this.value.prescriptions?.map(p => Object.assign(new Prescription(), p)) ?? [];
        this.activePatients = this.value.patients?.map(p => Object.assign(new Patient(), p)) ?? [];
        const assignments = this.value.assignments?.map(p => ({
            id: p.patientId ? `patient-${p.patientId}` : p.prescriberId ? `prescriber-${p.prescriberId}` : p.teamId ? p.teamId : p.userName,
            name: p.patientId ? Object.assign(new Patient(), p.patient).toString() : p.prescriberId ? Object.assign(new Prescriber(), p.prescriber).toString() : p.teamId ? p.teamName : p.userName,
            displayName: p.patientId ? Object.assign(new Patient(), p.patient).displayNamesForPerson() : p.prescriberId ? Object.assign(new Prescriber(), p.prescriber).displayNamesForPerson() : undefined,
            isUser: !p.teamId && !p.patientId && !p.prescriberId,
            isTeam: !!p.teamId,
            isPatient: !!p.patientId,
            isPrescriber: !!p.prescriberId
        })) ?? [];

        // Handle assignment display based on configuration and existing data
        // IMPORTANT: If task already has multiple assignments, preserve them even if feature is disabled
        // This prevents data loss when editing existing tasks with multiple assignments
        if (this.allowMultipleAssignments || assignments.length <= 1) {
            // Normal case: feature enabled, or only 0-1 assignments exist
            this.activeAssignedTo = this.allowMultipleAssignments ? assignments : (assignments.length > 0 ? assignments[0] : null);
        } else {
            // Special case: feature disabled but task has multiple assignments
            // Keep as array to preserve data, multiselect will still render correctly
            this.activeAssignedTo = assignments;
            // Temporarily allow multiple for this task to prevent data loss
            this.allowMultipleAssignments = true;
        }

        this.activeWatchers = this.value.watchers?.map(p => ({
            id: p.teamId ? p.teamId : p.userName,
            name: p.teamId ? p.teamName : p.userName,
            isTeam: !!p.teamId
        })) ?? [];
        this.activeStatus = this.value.status ?? UserTaskStatus.new;
        this.activeAnswers = this.value.completionAnswers ?? [];
        this.onActiveAssignedToChange();
        this.activeCompletionForm = this.value.taskCompletionForm ?
            Object.assign(new CustomFaxTemplateBase(), this.value.taskCompletionForm) : new CustomFaxTemplateBase();
    }

    get primaryPatient() {
        if (this.value.patients?.length !== 1) return null;
        return this.value.patients[0];
    }

    get primaryPrescriber() {
        if (this.value.prescriptions?.length !== 1) return null;
        return this.value.prescriptions[0].prescriber;
    }

    get isAssignedToMe(): boolean {
        const userName = this.$user?.userName;
        if (!userName) return false;

        if (Array.isArray(this.activeAssignedTo)) {
            return this.activeAssignedTo.some((a: any) => a.id === userName && a.isUser);
        } else if (this.activeAssignedTo) {
            return this.activeAssignedTo.id === userName && this.activeAssignedTo.isUser;
        }

        return false;
    }

    get limitToTeams() {
        return this.value.limitToTeamIds?.map(tid => this.teams.find(t => t.id === tid));
    }

    set limitToTeams(value) {
        if (!value) {
            this.value.limitToTeamIds = undefined;
        } else {
            this.value.limitToTeamIds = value!.map(i => i!.id);
        }
    }

    get limitToPrograms() {
        return this.value.limitToProgramIds?.map(pid => this.programs.find(p => p.id === pid));
    }

    set limitToPrograms(value) {
        if (!value) {
            this.value.limitToProgramIds = undefined;
        } else {
            this.value.limitToProgramIds = value!.map(i => i!.id);
        }
    }

    get limitToRxStatuses() {
        return this.value.limitToRxStatuses?.map(pid => this.rxStatusOptions.find(p => p.id === pid));
    }

    set limitToRxStatuses(value) {
        if (!value) {
            this.value.limitToRxStatuses = undefined;
        } else {
            this.value.limitToRxStatuses = value!.map(i => i!.id as RefillStatusCode);
        }
    }

    @Watch("value.status")
    handleStatusChange() {
        this.activeStatus = this.value.status;
    }

    @Watch("activeCompletionForm")
    handleCompletionFormChange() {
        this.$emit('input', {
            ...this.value,
            taskCompletionFormId: this.activeCompletionForm?.id,
            taskCompletionForm: this.activeCompletionForm
        });
    }

    @Watch("showCompletionQuestionErrors")
    handleShowCompletionQuestionErrorsChange() {
        if (this.showCompletionQuestionErrors) this.activeTab = 1;
    }

    async getTeams() {
        try {
            const response = await this.$http.get<Team[]>("/user/teams");
            this.teams = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async getUsers() {
        try {
            const response = await this.$http.get<User[]>("/user");
            this.users = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    protected showTemplateSelectionPopover = false;

    async getTemplates() {
        try {
            const response = await this.$http.get<UserTask[]>("/task/templates");
            this.templates = response.data;

            if (this.isNewTask && this.disableAdHocFields) {
                this.showTemplateSelectionPopover = true;
            }
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getPrograms() {
        try {
            const response = await this.$http.get<Program[]>("/program");
            this.programs = response.data;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    get rxStatusOptions() {
        return Object.entries(RefillStatusCode)
            .filter(([key]) => isNaN(Number(key)))
            .map(([name, id]) => ({ name, id }));
    }

    get assignToOptions() {

        const defaultOptions = [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            },
            {
                type: 'Users',
                options: this.users.map(u => ({
                    id: u.userName,
                    name: u.userName,
                    isUser: true
                }))
            }
        ];

        const activeAssigned = !this.activeAssignedTo ? null : (Array.isArray(this.activeAssignedTo) ? this.activeAssignedTo : [this.activeAssignedTo]);

        let patients: any[] = [];
        let prescribers: any[] = [];

        const isDuplicate = (array: any[], item: any) => array.some(a => a.id === item.id);

        if (activeAssigned?.find((a) => a.isPatient)) {
            patients = activeAssigned.filter((a) => a.isPatient);
        }

        if (activeAssigned?.find((a) => a.isPrescriber)) {
            prescribers = activeAssigned.filter((a) => a.isPrescriber);
        }

        if (this.patientOptions.length) {
            this.patientOptions.forEach(po => {
                const patient = {
                    id: `patient-${po.id}`,
                    name: Object.assign(new Patient(), po).toString(),
                    displayName: Object.assign(new Patient(), po).displayNamesForPerson(),
                    isPatient: true
                };
                if (!isDuplicate(patients, patient)) {
                    patients.push(patient);
                }
            });
        }

        if (this.prescriberOptions.length) {
            this.prescriberOptions.forEach(po => {
                const prescriber = {
                    id: `prescriber-${po.id}`,
                    name: Object.assign(new Prescriber(), po).toString(),
                    displayName: Object.assign(new Prescriber(), po).displayNamesForPerson(),
                    isPrescriber: true
                };
                if (!isDuplicate(prescribers, prescriber)) {
                    prescribers.push(prescriber);
                }
            });
        }

        if (this.value.patients) {
            this.value.patients.forEach(po => {
                const patient = {
                    id: `patient-${po.id}`,
                    name: Object.assign(new Patient(), po).toString(),
                    displayName: Object.assign(new Patient(), po).displayNamesForPerson(),
                    isPatient: true
                };
                if (!isDuplicate(patients, patient)) {
                    patients.push(patient);
                }
            });
        }

        if (this.value.prescriptions) {
            this.value.prescriptions.forEach(po => {
                if (!po.prescriber) return;
                const prescriber = {
                    id: `prescriber-${po.prescriber.id}`,
                    name: Object.assign(new Prescriber(), po.prescriber).toString(),
                    displayName: Object.assign(new Prescriber(), po.prescriber).displayNamesForPerson(),
                    isPrescriber: true
                };
                if (!isDuplicate(prescribers, prescriber)) {
                    prescribers.push(prescriber);
                }
            });
        }

        if (patients.length) {
            defaultOptions.push({
                type: 'Patients',
                options: patients
            })
        }

        if (prescribers.length) {
            defaultOptions.push({
                type: 'Prescribers',
                options: prescribers
            })
        }

        return defaultOptions;
    }

    get watchersOptions() {
        return [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            },
            {
                type: 'Users',
                options: this.users.map(u => ({
                    id: u.userName,
                    name: u.userName
                }))
            }
        ]
    }

    get loadingAssignToOptions() {
        return this.loadingPatientOptions || this.loadingPrescriberOptions;
    }

    private assignToSearchCancelTokenSource: CancelTokenSource | null = null;

    private assignToSearch(searchQuery: string) {
        this.assignToSearchCancelTokenSource?.cancel("New search started");
        this.doDebouncedAssignToSearch(searchQuery, this);
    }

    private doDebouncedAssignToSearch = debounce(500, async (searchQuery, context) => {
        if (!searchQuery?.trim()) {
            context.patientOptions = [];
            context.prescriberOptions = [];
            return;
        }

        context.loadingPatientOptions = true;
        context.loadingPrescriberOptions = true;

        context.assignToSearchCancelTokenSource = Axios.CancelToken.source();

        const patientOptionsPromise = Axios.get<Patient[]>("/patient", {
            params: { searchTerm: searchQuery },
            cancelToken: context.assignToSearchCancelTokenSource.token
        });
        const prescriberOptionsPromise = Axios.get<Prescriber[]>("/prescriber", {
            params: { searchTerm: searchQuery },
            cancelToken: context.assignToSearchCancelTokenSource.token
        });

        try {
            context.patientOptions = (await patientOptionsPromise).data;
        } catch {

        }

        try {
            context.prescriberOptions = (await prescriberOptionsPromise).data;
        } catch {

        }

        context.loadingPatientOptions = false;
        context.loadingPrescriberOptions = false;
    });

    @Watch("activePatient")
    addPatient(patient?: Patient) {
        if (!patient) {
            patient = this.activePatient;
        }

        if (!patient?.id) return;
        this.activePatients.push(patient);
        this.activePatient = new Patient();

        this.$emit("input", {
            ...this.value,
            patients: this.activePatients
        });
    }

    assignToPrimaryPatient() {
        if (!this.primaryPatient) return;
        this.activeAssignedTo = {
            id: `patient-${this.primaryPatient.id}`,
            name: Object.assign(new Patient(), this.primaryPatient).toString(),
            displayName: Object.assign(new Patient(), this.primaryPatient).displayNamesForPerson(),
            isPatient: true
        };
    }

    assignToPrimaryPrescriber() {
        if (!this.primaryPrescriber) return;
        this.activeAssignedTo = {
            id: "prescriber-" + this.primaryPrescriber.id,
            name: Object.assign(new Prescriber(), this.primaryPrescriber).toString(),
            displayName: Object.assign(new Prescriber(), this.primaryPrescriber).displayNamesForPerson(),
            isPrescriber: true
        };
    }

    removePatient(item: Patient) {
        this.activePatients = this.activePatients.filter(p => p.id !== item.id);

        this.$emit("input", {
            ...this.value,
            patients: this.activePatients
        });
    }

    patientAddSearchResultFilter(patients: Patient[]) {
        return patients.filter(p => !this.activePatients.some(ap => ap.id === p.id));
    }

    @Watch("activePrescription")
    async addPrescription(prescription?: Prescription) {
        if (!prescription) {
            prescription = this.activePrescription;
        }

        if (!prescription?.rxNumber) return;

        if (!prescription.prescriber) {
            try {
                const prescriberResponse = await this.$http.get<Prescriber>(`/Prescriber/${prescription.prescriberID}`);
                prescription.prescriber = prescriberResponse.data;
            } catch {
                //ignore
            }
        }

        this.activePrescriptions.push(prescription);
        this.activePrescription = new Prescription();

        this.$emit("input", {
            ...this.value,
            prescriptions: this.activePrescriptions
        });
    }

    removePrescription(item: Prescription) {
        this.activePrescriptions = this.activePrescriptions.filter(p => !(p.storeID == item.storeID &&
            p.rxNumber == item.rxNumber &&
            p.rfNumber == item.rfNumber));

        this.$emit("input", {
            ...this.value,
            prescriptions: this.activePrescriptions
        });
    }

    prescriptionAddSearchResultFilter(prescriptions: Prescription[]) {
        return prescriptions.filter(p => !this.activePrescriptions.some(ap => ap.storeID == p.storeID &&
            ap.rxNumber == p.rxNumber &&
            ap.rfNumber == p.rfNumber));
    }

    prescriptionOptionLabel(prescription: Prescription) {
        const baseLabel = prescription.toString();
        const drugName = prescription.drugName || '';
        return drugName ? `${baseLabel} - Drug: ${drugName}` : baseLabel;
    }

    @Watch("activeStore")
    onActiveStoreChange() {
        this.$emit('input', {
            ...this.value,
            storeId: this.activeStore?.id
        })
    }

    @Watch("value.startDate")
    onStartDateChange(_: Date, oldValue: Date) {
        if (this.value.isScheduled) {
            // Scheduled tasks: always maintain the day offset
            const dayDiff = moment(this.value.dueDate).startOf('day')
                .diff(moment(oldValue).startOf('day'), 'days');
            this.value.dueDate = moment(this.value.startDate).add(dayDiff, "days").toDate();
        } else if (!this.value.isTemplate) {
            // Non-scheduled tasks: only update Due Date if Start Date moves past it
            const newStartDate = moment(this.value.startDate).startOf('day');
            const currentDueDate = moment(this.value.dueDate).startOf('day');

            if (newStartDate.isAfter(currentDueDate)) {
                // Start Date moved past Due Date - maintain the original day gap
                const dayDiff = currentDueDate.diff(moment(oldValue).startOf('day'), 'days');
                this.value.dueDate = moment(this.value.startDate).add(dayDiff, "days").toDate();
            }
        }
    }

    @Watch("activeAssignedTo")
    onActiveAssignedToChange() {
        const assignedToIsArray = Array.isArray(this.activeAssignedTo);

        let assignments = [];

        if (assignedToIsArray) {
            assignments = this.activeAssignedTo;
        } else if (this.activeAssignedTo) {
            assignments = [this.activeAssignedTo];
        }

        assignments = assignments.map((a: any) => ({
            teamId: a.isTeam ? a.id : undefined,
            userName: a.isUser ? a.name : undefined,
            patientId: a.isPatient ? a.id.replace("patient-", "") : undefined,
            prescriberId: a.isPrescriber ? a.id.replace("prescriber-", "") : undefined
        }));

        this.$emit('input', {
            ...this.value,
            assignments
        });
    }

    @Watch("activeWatchers")
    onActiveWatchersChange() {
        const watchers = (Array.isArray(this.activeWatchers) ? this.activeWatchers : [this.activeWatchers]).map(a => ({
            teamId: a.isTeam ? a.id : undefined,
            userName: !a.isTeam ? a.id : undefined
        }));

        this.$emit('input', {
            ...this.value,
            watchers
        });
    }

    assignToMe() {
        const userName = this.$user?.userName;
        if (!userName) return;

        const meAssignment = {
            id: userName,
            name: userName,
            isUser: true
        };

        // If multiple assignments allowed, add to existing assignments
        // Otherwise, replace with just "me"
        if (this.allowMultipleAssignments) {
            const existingAssignments = Array.isArray(this.activeAssignedTo) ? this.activeAssignedTo : [];
            // Check if already assigned to me
            if (existingAssignments.some((a: any) => a.id === userName)) return;
            this.activeAssignedTo = [...existingAssignments, meAssignment];
        } else {
            this.activeAssignedTo = meAssignment;
        }
    }

    addMeAsWatcher() {
        const userName = this.$user?.userName;
        if (!userName || this.activeWatchers.find((w: any) => w.id?.toLowerCase() == userName.toLowerCase())) return;
        this.activeWatchers.push({
            id: userName,
            name: userName
        });
    }

    updateActiveStatus(value: UserTaskStatus) {
        this.activeStatus = value;

        this.$emit('input', {
            ...this.value,
            status: value
        })
    }

    refreshNotes() {
        this.notesList?.loadNotes();
    }

    async savePendingNote() {
        // Check if there's a note form with unsaved content
        if (!this.notesForm || !this.value.id) {
            return;
        }

        // Access the note body from the NotesForm component
        const noteBody = this.notesForm.note?.body;

        // If there's unsaved content, save it
        if (noteBody && noteBody.trim()) {
            await this.notesForm.save();
        }
    }

    handleQuestionsUpdate(updatedQuestions: UserTaskQuestion[]) {
        const updatedValue = {
            ...this.value,
            completionQuestionSet: {
                ...(this.value.completionQuestionSet || {
                    questions: [],
                    entityType: EntityType.UserTask,
                    active: true
                }),
                questions: updatedQuestions
            }
        };
        this.$emit('input', updatedValue);
    }

    get getScheduleTypeOptions() {
        return [
            { text: "Day Interval", value: 0 },
            { text: "Days of Week", value: 1 },
            { text: "Days of Month", value: 2 }
        ]
    }

    handleDaySchedule(index: number) {
        let days = this.value.repeatSchedule?.split(",") ?? [];

        if (days?.includes(index.toString())) {
            days = days.filter(d => d !== index.toString());
        } else {
            days.push(index.toString());
        }

        days = days.filter(d => d);

        this.$emit('input', {
            ...this.value,
            repeatSchedule: days.join(",")
        });
    }

    handleMonthSchedule(day: number) {
        let days = this.value.repeatSchedule?.split(",") ?? [];

        if (days?.includes(day.toString())) {
            days = days.filter(d => d !== day.toString());
        } else {
            days.push(day.toString());
        }

        days = days.filter(d => d);

        days.sort((a, b) => Number(a) - Number(b));

        this.$emit('input', {
            ...this.value,
            repeatSchedule: days.join(",")
        });
    }

    get dueDateDaysAfter() {
        const dayDiff = moment(this.value.dueDate).startOf('day')
            .diff(moment(this.value.startDate).startOf('day'), 'days');

        return dayDiff ? dayDiff : undefined;
    }

    get expirationDaysAfter() {
        if (!this.value.expiredOnDate) return undefined;

        const dayDiff = moment(this.value.expiredOnDate).startOf('day')
            .diff(moment(this.value.startDate).startOf('day'), 'days');

        return dayDiff ? dayDiff : undefined;
    }

    handleDueAfterCreation(days: string) {
        this.handleDateTimeChange(moment(this.value.startDate).add(days, 'days').toDate(), moment.utc(this.value.dueDate).local().format("HH:mm:ss"));
    }

    handleExpiresAfterCreation(days?: string) {
        if (!days || days === "0") {
            this.value.expiredOnDate = undefined;
            return;
        }
        this.value.expiredOnDate = moment(this.value.startDate).add(days, 'days').toDate();
    }

    async applyTemplate(task: UserTask) {
        try {
            task = (await this.$http.get<UserTask>(`/task/${task.id}`)).data;

            const dueDayDiff = moment(task.dueDate).startOf('day')
                .diff(moment(task.startDate).startOf('day'), 'days');
            const expiresDayDiff = task.expiredOnDate ? moment(task.expiredOnDate).startOf('day')
                .diff(moment(task.startDate).startOf('day'), 'days') : undefined;

            this.$emit('input', {
                ...task,
                patients: this.value.patients,
                prescriptions: this.value.prescriptions,
                startDate: moment(),
                dueDate: moment().add(dueDayDiff, "days"),
                expiredOnDate: expiresDayDiff ? moment().add(expiresDayDiff, "days") : undefined,
                id: 0,
                isTemplate: this.value.isTemplate,
                isScheduled: this.value.isScheduled,
                createdByTemplateId: task.id
            })
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get filteredTemplates() {
        return this.templates.filter(t => {
            if (!t.limitToProgramIds?.length) return true;

            let shouldInclude = false;

            t.limitToProgramIds.forEach(lpid => {
                if (this.value.prescriptions?.some(p => p.programID == lpid)) {
                    shouldInclude = true;
                }
            });

            return shouldInclude;
        }).filter(t => {
            if (!t.limitToRxStatuses?.length) return true;

            let shouldInclude = false;

            t.limitToRxStatuses.forEach(lpid => {
                if (this.value.prescriptions?.some(p => p.status == lpid)) {
                    shouldInclude = true;
                }
            });

            return shouldInclude;
        })
            .filter(t => t.title.toLowerCase().includes(this.filterTemplates.toLowerCase()));
    }

    getPatientHtml(patient: Patient) {
        return `<a href='/processing/patient-dashboard/${patient.id}' target='_blank'>${patient.toString()}</a>`;
    }

    getPrescriptionHtml(prescription: Prescription) {
        const displayText = this.prescriptionOptionLabel(prescription);
        return `<a href='/processing/prescription/${prescription.storeID}/${prescription.rxNumber}/${prescription.rfNumber}' target='_blank'>${displayText}</a>`;
    }

    handleDateTimeChange(newDate: any, newTime: any) {
        if (!newDate) newDate = moment(this.value.dueDate).toDate() ?? new Date();
        if (!newTime) newTime = moment(this.value.dueDate).format("HH:mm:ss") ?? "12:00:00";

        const combinedDateTime = moment(newDate).set({
            hour: parseInt(newTime.split(':')[0]),
            minute: parseInt(newTime.split(':')[1]),
            second: parseInt(newTime.split(':')[2])
        });

        this.$emit("input", { ...this.value, dueDate: combinedDateTime.toDate() });
    }

    handleExpiresDateChange(newDate: any) {

    }

    handleDateChange(newDate: any) {
        this.handleDateTimeChange(newDate, moment.utc(this.value.dueDate).local().format("HH:mm:ss"));
    }

    handleTimeChange(newTime: any) {
        this.handleDateTimeChange(moment(this.value.dueDate).toDate(), newTime);
    }

    get hideTabs() {
        if (this.value.isScheduled || this.value.isTemplate) return false;
        if (!this.value.id) return false;
        return !this.value.completionQuestionSet?.questions?.length && !this.value.completionFormImageId;
    }

    get showQuestionsTab() {
        if (this.value.isScheduled || this.value.isTemplate) return true;
        if (!this.value.id) return true;
        return this.value.completionQuestionSet?.questions.length;
    }

    resetActiveTab() {
        setTimeout(() => {
            this.activeTab = 0;
        }, 250);
    }

    get hasAdHocTaskPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Processing/Ad Hoc Tasks");
    }

    get hasTaskAdminPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Admin/Tasks");
    }

    get isRegularTask() {
        return !this.value.isScheduled && !this.value.isTemplate;
    }

    get isNewTask() {
        return (this.value.id ?? 0) === 0;
    }

    get canCreateAdHocTask() {
        return this.hasAdHocTaskPermission || this.hasTaskAdminPermission;
    }

    get disableAdHocFields() {
        return this.isRegularTask && !this.canCreateAdHocTask;
    }
}

</script>

<style>
.task-template-popover {
    width: 300px;
    max-width: 300px;
}

.task-template-popover .scroller {
    overflow-y: auto;
    max-height: 60vh;
}
</style>

<style scoped lang="scss">
.card {
    box-shadow: none;
}

.status.dropdown {
    .badge {
        display: block;
    }

    ::v-deep(.btn) {
        border: none !important;
        box-shadow: none;
        background: none;
        margin-top: 6px;
    }
}

textarea.form-control {
    max-height: 240px;
}

.schedule-month-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
}

::v-deep(.action-col) {
    width: 50px;
}

::v-deep(.b-form-timepicker) {
    height: 34px !important;
}

::v-deep(li:has(.multiselect__option--disabled)) {
    position: sticky;
    top: 0;
    z-index: 100;
}
</style>
